import { fetchWithAuth } from "../utils/auth";
import { API_URL } from "../utils/constants";
import { ILease } from "./leaseService";
import { IPropertyManagementContract } from "./pmContractService";
import { IUnit, IUnitOwner } from "./unitService";
import { TFunction } from "i18next";

export enum DocumentType {
  PASSPORT = "passport",
  PROOF_OF_ADDRESS = "proof-of-address",
  EMIRATES_ID = "emirates-id",
  VISA = "visa",
  TENANCY_CONTRACT = "tenancy-contract",
  EJARI = "ejari",
  PURCHASE_CONTRACT = "purchase-contract",
  TITLE_DEED = "title-deed",
  OQOOD = "oqood",
  POWER_OF_ATTORNEY = "power-of-attorney",
  PROPERTY_MANAGEMENT_CONTRACT = "property-management-contract",
  SNAGGING_REPORT = "snagging-report",
  DE_SNAGGING_REPORT = "de-snagging-report",
  INVOICE = "invoice",
  RECEIPT = "receipt",
  SALARY_CERTIFICATE = "salary-certificate",
}

export const formatDocumentType = (documentType: DocumentType | null) => {
  switch (documentType) {
    case DocumentType.PASSPORT:
      return "Reisepass";
    case DocumentType.PROOF_OF_ADDRESS:
      return "Wohnsitznachweis";
    case DocumentType.EMIRATES_ID:
      return "Emirates ID";
    case DocumentType.VISA:
      return "UAE VISA";
    case DocumentType.TENANCY_CONTRACT:
      return "Mietvertrag";
    case DocumentType.EJARI:
      return "EJARI";
    case DocumentType.SNAGGING_REPORT:
      return "Snagging Report";
    case DocumentType.DE_SNAGGING_REPORT:
      return "De-Snagging Report";
    case DocumentType.PURCHASE_CONTRACT:
      return "Kaufvertrag";
    case DocumentType.TITLE_DEED:
      return "Title Deed";
    case DocumentType.OQOOD:
      return "Oqood";
    case DocumentType.POWER_OF_ATTORNEY:
      return "Vollmacht";
    case DocumentType.PROPERTY_MANAGEMENT_CONTRACT:
      return "Verwaltungsvertrag";
    case DocumentType.INVOICE:
      return "Rechnung";
    case DocumentType.RECEIPT:
      return "Quittung";
    case DocumentType.SALARY_CERTIFICATE:
      return "Gehaltsnachweis";
    default:
      return "Unbekannt";
  }
};

export const formatDocumentTypeWithTranslation =
  (t: TFunction) => (documentType: DocumentType | null) => {
    switch (documentType) {
      case DocumentType.PASSPORT:
        return t("document_types.passport", "Reisepass");
      case DocumentType.PROOF_OF_ADDRESS:
        return t("document_types.proof_of_address", "Wohnsitznachweis");
      case DocumentType.EMIRATES_ID:
        return t("document_types.emirates_id", "Emirates ID");
      case DocumentType.VISA:
        return t("document_types.visa", "UAE VISA");
      case DocumentType.TENANCY_CONTRACT:
        return t("document_types.tenancy_contract", "Mietvertrag");
      case DocumentType.PURCHASE_CONTRACT:
        return t("document_types.purchase_contract", "Kaufvertrag");
      case DocumentType.TITLE_DEED:
        return t("document_types.title_deed", "Title Deed");
      case DocumentType.OQOOD:
        return t("document_types.oqood", "Oqood");
      case DocumentType.POWER_OF_ATTORNEY:
        return t("document_types.power_of_attorney", "Vollmacht");
      case DocumentType.PROPERTY_MANAGEMENT_CONTRACT:
        return t(
          "document_types.property_management_contract",
          "Verwaltungsvertrag"
        );
      case DocumentType.INVOICE:
        return t("document_types.invoice", "Rechnung");
      case DocumentType.RECEIPT:
        return t("document_types.receipt", "Quittung");
      case DocumentType.SALARY_CERTIFICATE:
        return t("document_types.salary_certificate", "Gehaltsnachweis");
      default:
        return t("document_types.unknown", "Unbekannt");
    }
  };

export enum DocumentStatus {
  ACCEPTED = "accepted",
  IN_REVIEW = "in_review",
  DECLINED = "declined",
}

export const formatDocumentStatus = (status: DocumentStatus) => {
  switch (status) {
    case DocumentStatus.ACCEPTED:
      return "Akzeptiert";
    case DocumentStatus.IN_REVIEW:
      return "In Prüfung";
    case DocumentStatus.DECLINED:
      return "Abgelehnt";
    default:
      return "Unbekannt";
  }
};

export const formatDocumentStatusWithTranslation =
  (t: TFunction) => (status: DocumentStatus) => {
    switch (status) {
      case DocumentStatus.ACCEPTED:
        return t("document_status.accepted", "Akzeptiert");
      case DocumentStatus.IN_REVIEW:
        return t("document_status.in_review", "In Prüfung");
      case DocumentStatus.DECLINED:
        return t("document_status.declined", "Abgelehnt");
      default:
        return t("document_status.unknown", "Unbekannt");
    }
  };

export interface IDocument {
  document_id: number;
  document_type?: DocumentType | null;
  status: DocumentStatus;
  reason_for_refusal: string | null;
  file_type: string;
  file_path: string;
  file_name: string;
  valid_until: string | null;
  internal_document: boolean;
  document_identifier: string | null;
  created_at: string;
  updated_at: string;
  unit?: IUnit | null;
  lease?: ILease | null;
  unit_owner?: IUnitOwner | null;
  property_management_contract?: IPropertyManagementContract | null;
}

export const downloadDocument = async (
  documentId: number
): Promise<Blob | null> => {
  const response = await fetchWithAuth(
    `${API_URL}/documents/${documentId}/download`,
    {
      method: "GET",
    }
  );

  if (!response.ok) {
    throw new Error("Network response was not ok " + response.statusText);
  }

  const fileBlobResponse = await response.blob();
  return fileBlobResponse;
};

export const deleteDocument = async (document_id: number): Promise<boolean> => {
  const response = await fetchWithAuth(`${API_URL}/documents/${document_id}`, {
    method: "DELETE",
  });

  return response.status === 200;
};
